import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { EventRiskBody, EventRisk } from '../types'

export function useHazardLibrary() {
  return useQuery({
    queryKey: ['hazard-library'],
    queryFn: ApiClient.hazardLibrary,
    staleTime: 60 * 60_000     // 1 hr - org-managed catalogue
  })
}

export function useEventRisks(eventId: number) {
  return useQuery({
    queryKey: ['event-risks', eventId],
    queryFn: () => ApiClient.eventRisks(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useCreateEventRisk(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventRiskBody) => ApiClient.createEventRisk(eventId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-risks', eventId] })
    }
  })
}

export function useCreateRisksFromLibrary(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (libraryIds: number[]) =>
      ApiClient.createRisksFromLibrary(eventId, libraryIds),
    onSuccess: (rows: EventRisk[]) => {
      qc.setQueryData(['event-risks', eventId], rows)
    }
  })
}

export function useUpdateEventRisk(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ riskId, body }: { riskId: number; body: EventRiskBody }) =>
      ApiClient.updateEventRisk(eventId, riskId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-risks', eventId] })
    }
  })
}

export function useSignOffEventRisk(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (riskId: number) => ApiClient.signOffEventRisk(eventId, riskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-risks', eventId] })
    }
  })
}

export function useDeleteEventRisk(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (riskId: number) =>
      ApiClient.deleteEventRisk(eventId, riskId).then(() => riskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-risks', eventId] })
    }
  })
}
